import styled from "@emotion/styled";
import React from "react";
import { observer } from "mobx-react-lite";
import { useLang } from "@src/hooks/useLang";
import { useStores } from "@stores";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";

const BulkBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 16px;
  background: #f5f6f7;
  border-bottom: 1px solid rgba(137, 144, 147, 0.2);
`;

const BulkBarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: #494c5b;
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #3d6e0e;
  &:disabled {
    color: #b7c0c4;
    cursor: default;
  }
`;

const BulkActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const BulkActionButton = styled.button<{ danger?: boolean }>`
  border: 1px solid ${({ danger }) => (danger ? "#e53e3e" : "#ccd5d8")};
  background: #fff;
  color: ${({ danger }) => (danger ? "#e53e3e" : "#494c5b")};
  font-family: Nunito Sans, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  &:hover:not(:disabled) {
    border-color: ${({ danger }) => (danger ? "#e53e3e" : "#68ad18")};
    background: ${({ danger }) => (danger ? "#fff5f5" : "#f9fdf5")};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

interface IProps {
  selectedCount: number;
  /** Every row the list currently shows is selected (the link then clears). */
  allSelected: boolean;
  onToggleAll: () => void;
  onChangeCrop: () => void;
  onMoveFarm: () => void;
  onExport: () => void;
  onComplete: () => void;
  onDelete: () => void;
}

/**
 * The selection-mode toolbar above the field list (moved out of
 * ViewFieldsMenu). The confirm dialogs stay with the list; this only opens
 * them. The John Deere / Cropwise sync buttons act directly and show only when
 * the selection holds a linked field.
 */
const BulkActionsBar: React.FC<IProps> = ({
  selectedCount,
  allSelected,
  onToggleAll,
  onChangeCrop,
  onMoveFarm,
  onExport,
  onComplete,
  onDelete,
}) => {
  const vm = useFieldsScreenVM();
  const lang = useLang();
  const { settingsStore } = useStores();
  return (
    <BulkBar>
      <BulkBarHeader>
        <span>
          {selectedCount}{" "}
          {lang.fields.selectedLabel}
        </span>
        <LinkButton onClick={onToggleAll}>
          {allSelected
            ? lang.fields.clearSelection
            : lang.fields.selectAll}
        </LinkButton>
      </BulkBarHeader>
      <BulkActions>
        <BulkActionButton disabled={selectedCount === 0} onClick={onChangeCrop}>
          {lang.fields.bulkChangeCrop}
        </BulkActionButton>
        <BulkActionButton disabled={selectedCount === 0} onClick={onMoveFarm}>
          {lang.farms.moveToFarm}
        </BulkActionButton>
        <BulkActionButton disabled={selectedCount === 0} onClick={onExport}>
          {lang.fields.bulkExport}
        </BulkActionButton>
        {vm.selectedHasJohnDeere && (
          <BulkActionButton
            disabled={selectedCount === 0 || vm.bulkSyncing}
            onClick={() => vm.bulkSyncToDeere()}
          >
            {lang.fields.bulkSyncToDeere}
          </BulkActionButton>
        )}
        {settingsStore.hasConfig("cropwise") && vm.selectedHasCropwise && (
          <BulkActionButton
            disabled={selectedCount === 0 || vm.bulkSyncing}
            onClick={() => vm.bulkSyncToCropwise()}
          >
            {lang.fields.bulkSyncToCropwise}
          </BulkActionButton>
        )}
        <BulkActionButton disabled={selectedCount === 0} onClick={onComplete}>
          {lang.fields.bulkComplete}
        </BulkActionButton>
        <BulkActionButton danger disabled={selectedCount === 0} onClick={onDelete}>
          {lang.fields.bulkDelete}
        </BulkActionButton>
      </BulkActions>
    </BulkBar>
  );
};

export default observer(BulkActionsBar);
